import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, Chip, EmptyState, Icon, Skeleton, Tabs } from "../components/ui";
import CharacterCard from "../components/character/Card";
import Grid from "../components/character/Grid";
import TagFilter from "../components/character/TagFilter";
import SwipeDeck from "../components/character/SwipeDeck";
import useInfinite from "../components/character/useInfinite";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useI18n } from "../services/i18n";
import content from "../services/api-content";
import "../styles/content.css";

const POPULAR = ["รุ่นพี่วิศวะ", "เพื่อนสนิท", "ตลก", "ฮีโร่", "ดูดาว", "มาเฟีย"];
const CODE_RE = /^[A-Za-z0-9_-]{7}$/;

export default function Search() {
  const { profile } = useAuth();
  const toast = useToast();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") || "text";
  const [input, setInput] = useState(params.get("q") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [tags, setTags] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeHit, setCodeHit] = useState(null);
  const [aiQ, setAiQ] = useState("");
  const [ai, setAi] = useState({ loading: false, data: null, error: "" });
  const allTags = useAsync(() => content.tags.list(), []);
  const popular = useAsync(() => content.characters.list({ sort: "popular", limit: 8 }), []);
  const searching = !!q || tags.length > 0;
  const list = useInfinite((page) => (searching ? content.characters.list({ q, tags, page, limit: 20, sort: "popular" }) : Promise.resolve({ items: [], hasMore: false })), [q, tags.join(",")]);

  // debounce คำค้น 350ms
  useEffect(() => {
    const id = setTimeout(() => setQ(input.trim()), 350);
    return () => clearTimeout(id);
  }, [input]);

  // พิมพ์เป็นโค้ด 7 ตัว -> ลองหาตัวละครจากโค้ดแชร์ด้วย
  useEffect(() => {
    setCodeHit(null);
    if (!CODE_RE.test(q)) return undefined;
    let alive = true;
    content.characters.byCode(q).then((c) => alive && setCodeHit(c)).catch(() => {});
    return () => { alive = false; };
  }, [q]);

  const setMode = (m) => setParams(m === "text" ? {} : { mode: m }, { replace: true });
  const findCode = async (e) => {
    e.preventDefault();
    setCodeBusy(true);
    try {
      const c = await content.characters.byCode(code);
      if (!c) toast("ไม่พบตัวละครจากโค้ดนี้", "error");
      setCodeHit(c);
    } catch (err) { toast(err.message, "error"); } finally { setCodeBusy(false); }
  };
  const runAi = async (e) => {
    e?.preventDefault();
    if (!aiQ.trim()) return;
    setAi({ loading: true, data: null, error: "" });
    try { setAi({ loading: false, data: await content.characters.aiSearch(aiQ.trim()), error: "" }); } catch (err) { setAi({ loading: false, data: null, error: err.message }); }
  };

  return (
    <div>
      <div className="page-head"><div><h1>{t("ค้นหา")}</h1><p>หาตัวละครจากชื่อ คำโปรย แท็ก หรือโค้ดแชร์</p></div></div>
      <Tabs tabs={[{ value: "text", label: "ค้นหา" }, { value: "ai", label: "✨ " + t("ค้นหาด้วย AI") }, { value: "swipe", label: t("ปัดตัวละคร") }]} value={mode} onChange={setMode} />

      {mode === "swipe" ? <div className="mt-24"><SwipeDeck userName={profile?.displayName} /></div> : null}

      {mode === "ai" ? (
        <div className="mt-16">
          <form onSubmit={runAi} className="stack">
            <div className="searchbox"><Icon name="sparkle" size={18} className="icon" /><input className="field__control" maxLength={200} aria-label="บอก AI ว่าอยากคุยกับใคร" placeholder="วันนี้อยากคุยกับใคร? เช่น รุ่นพี่ใจดีที่ชอบติวหนังสือ" value={aiQ} onChange={(e) => setAiQ(e.target.value)} /></div>
            <div className="row wrap"><Button type="submit" variant="primary" disabled={ai.loading || !aiQ.trim()}>{ai.loading ? "AI กำลังค้นหา..." : "ให้ AI ช่วยค้นหา (ฟรี)"}</Button>
              {["รุ่นพี่ใจดี", "เพื่อนตลก ๆ", "ตัวละครแฟนตาซี"].map((s) => <Chip key={s} size="sm" onClick={() => setAiQ(s)}>{s}</Chip>)}</div>
          </form>
          <div className="mt-16">
            {ai.error ? <div className="error-text" role="alert">{ai.error}</div> : null}
            {ai.loading ? <div className="grid-cards">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={260} radius={16} />)}</div> : null}
            {ai.data ? (
              <>
                <p className="muted mb-16">AI เข้าใจว่า: {[...(ai.data.interpreted?.keywords || []), ...(ai.data.interpreted?.tags || [])].join(", ") || "-"}{ai.data.mock ? " (โหมดจำลอง)" : ""}</p>
                {ai.data.items.length ? <div className="grid-cards">{ai.data.items.map((c) => <CharacterCard key={c.id} c={c} userName={profile?.displayName} />)}</div> : <EmptyState icon="🤔" title="ไม่พบตัวละครที่ตรง">ลองอธิบายใหม่ด้วยคำอื่น</EmptyState>}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {mode === "text" ? (
        <>
          <div className="row mt-16">
            <div className="grow searchbox"><Icon name="search" size={18} className="icon" /><input className="field__control" type="search" autoFocus aria-label="ค้นหาตัวละคร" placeholder="ค้นหาตัวละคร หรือใส่โค้ดแชร์..." value={input} onChange={(e) => setInput(e.target.value)} /></div>
            <Button onClick={() => setFilterOpen(true)} aria-label="ตัวกรองแท็ก">แท็ก{tags.length ? ` (${tags.length})` : ""}</Button>
          </div>
          {tags.length ? <div className="chip-row mt-8">{tags.map((x) => <Chip key={x} active onClick={() => setTags(tags.filter((y) => y !== x))}>{x} ✕</Chip>)}<Chip onClick={() => setTags([])}>ล้างทั้งหมด</Chip></div> : null}

          <Card className="mt-16">
            <form onSubmit={findCode} className="row wrap">
              <div className="grow" style={{ minWidth: 220 }}><div className="strong">ค้นหาด้วยโค้ดแชร์ตัวละคร</div><div className="muted" style={{ fontSize: 13 }}>มีโค้ด 7 ตัวจากเพื่อน? ใส่ตรงนี้เลย</div></div>
              <input className="field__control" style={{ width: 170 }} maxLength={40} aria-label="โค้ดแชร์ตัวละคร" placeholder="เช่น aB3dE9f" value={code} onChange={(e) => setCode(e.target.value.trim())} />
              <Button type="submit" disabled={codeBusy || code.length < 2}>{codeBusy ? "..." : "ค้นหาโค้ด"}</Button>
            </form>
          </Card>
          {codeHit ? <Card grad className="mt-16 row"><div className="grow"><div className="faint" style={{ fontSize: 12 }}>พบจากโค้ดแชร์</div><div className="strong">{codeHit.name}</div><div className="muted clamp-1">{codeHit.tagline}</div></div><Link to={`/c/${codeHit.id}`} className="btn btn--primary">เปิดตัวละคร</Link></Card> : null}

          {!searching ? (
            <>
              <h2 className="mt-24 mb-8">คำค้นยอดนิยม</h2>
              <div className="chip-row mb-16">{POPULAR.map((p) => <Chip key={p} onClick={() => setInput(p)}>{p}</Chip>)}</div>
              <h2 className="mb-16">ตัวละครยอดนิยม</h2>
              {popular.error ? <div className="error-text">{popular.error}</div> : popular.loading ? <Skeleton height={240} radius={16} /> : <div className="grid-cards">{(popular.data?.items || []).map((c) => <CharacterCard key={c.id} c={c} userName={profile?.displayName} />)}</div>}
            </>
          ) : (
            <div className="mt-24">
              <Grid loading={list.loading} error={list.error} onRetry={list.reload} count={list.items.length} hasMore={list.hasMore} onMore={list.loadMore} moreLoading={list.more}
                empty={<EmptyState icon="🔎" title="ไม่พบผลลัพธ์" action={<Button onClick={() => { setMode("ai"); setAiQ(q); }}>ลองให้ AI ช่วยค้นหา</Button>}>ลองคำค้นอื่น หรือลดแท็กที่เลือก</EmptyState>}>
                {list.items.map((c) => <CharacterCard key={c.id} c={c} userName={profile?.displayName} />)}
              </Grid>
            </div>
          )}
        </>
      ) : null}
      <TagFilter open={filterOpen} onClose={() => setFilterOpen(false)} tags={allTags.data || []} selected={tags} onChange={setTags} title="เลือกแท็ก" />
    </div>
  );
}
